import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { AUTH } from "../lib/testIds";
import { resetPassword } from "../lib/api";

const formatDetail = (detail) => {
    if (!detail) return null;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
            .filter(Boolean)
            .join(" · ");
    }
    if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
    return null;
};

export const ResetPassword = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (!token) {
        return (
            <main
                data-testid={AUTH.resetInvalid}
                className="relative z-10 mx-auto flex min-h-[70vh] max-w-lg items-center px-6 py-14 md:px-10 md:py-20"
            >
                <div className="w-full rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 text-center shadow-[0_20px_60px_rgba(46,50,32,0.10)] md:p-9">
                    <h1 className="font-display text-3xl font-bold text-[#2e3220]">
                        Missing reset link
                    </h1>
                    <p className="mt-2 text-sm text-[#5a6142]">
                        Open the reset link from the email we sent you — this URL
                        doesn&apos;t include a token.
                    </p>
                    <Link
                        to="/forgot-password"
                        className="btn-primary mt-6 inline-flex"
                    >
                        Request a new link
                    </Link>
                </div>
            </main>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (password.length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords don't match.");
            return;
        }
        try {
            setSubmitting(true);
            await resetPassword({ token, newPassword: password });
            toast.success("Password reset", {
                description: "Sign in with your new password.",
            });
            navigate("/login", { replace: true });
        } catch (err) {
            setError(
                formatDetail(err?.response?.data?.detail) ||
                    err?.message ||
                    "Couldn't reset your password. Try requesting a new link.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main
            data-testid={AUTH.resetPage}
            className="relative z-10 mx-auto flex min-h-[70vh] max-w-lg items-center px-6 py-14 md:px-10 md:py-20"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 -right-16 h-[320px] w-[320px] rounded-full bg-[#737a3d]/12 blur-3xl"
            />
            <div className="relative w-full rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 shadow-[0_20px_60px_rgba(46,50,32,0.10)] md:p-9">
                <Link
                    to="/login"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#737a3d] hover:underline"
                >
                    <ArrowLeft className="h-3 w-3" strokeWidth={2.5} />
                    Back to sign in
                </Link>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#2e3220]/10 bg-white/60 px-3 py-1.5">
                    <ShieldCheck className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                    <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                        New password
                    </span>
                </div>
                <h1 className="mt-3 font-display text-3xl font-bold leading-tight text-[#2e3220] sm:text-4xl">
                    Choose a new password
                </h1>
                <p className="mt-2 text-sm leading-relaxed text-[#5a6142]">
                    Set a password of at least 8 characters. We&apos;ll sign you
                    out everywhere else once you save it.
                </p>

                <form
                    onSubmit={handleSubmit}
                    data-testid={AUTH.resetForm}
                    className="mt-7 space-y-3"
                    noValidate
                >
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            New password
                        </span>
                        <div className="relative mt-1">
                            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5a6142]" />
                            <input
                                type={showPw ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid={AUTH.resetPasswordInput}
                                autoComplete="new-password"
                                placeholder="At least 8 characters"
                                className="w-full rounded-2xl border border-[#2e3220]/15 bg-white/70 py-3 pl-9 pr-10 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                data-testid={AUTH.showPasswordToggle}
                                aria-label={showPw ? "Hide password" : "Show password"}
                                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-[#5a6142] hover:bg-[#2e3220]/5"
                            >
                                {showPw ? (
                                    <EyeOff className="h-4 w-4" strokeWidth={2} />
                                ) : (
                                    <Eye className="h-4 w-4" strokeWidth={2} />
                                )}
                            </button>
                        </div>
                    </label>

                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            Confirm password
                        </span>
                        <div className="relative mt-1">
                            <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5a6142]" />
                            <input
                                type={showPw ? "text" : "password"}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                data-testid={AUTH.resetConfirmInput}
                                autoComplete="new-password"
                                placeholder="Type it again"
                                className="w-full rounded-2xl border border-[#2e3220]/15 bg-white/70 py-3 pl-9 pr-3 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                            />
                        </div>
                    </label>

                    {error && (
                        <p
                            data-testid={AUTH.error}
                            role="alert"
                            className="rounded-2xl border border-[#B56C5A]/40 bg-[#B56C5A]/10 px-3 py-2 text-xs font-semibold text-[#7a3a30]"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid={AUTH.resetSubmit}
                        className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        Save new password
                    </button>
                </form>
            </div>
        </main>
    );
};

export default ResetPassword;
